import { useMutation } from '@tanstack/react-query';
import {
  completePasswordReset,
  requestPasswordReset,
  type CompletePasswordResetInput,
} from '../api/password-reset.api';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { getApiErrorCode } from '@/lib/utils/error.utils';

/**
 * Request a password reset code (request -> reset, MYN-133 / FR-05b).
 * `onRequested` lives on the mutation definition, not the `.mutate()` call
 * site, so it still fires (advancing `ForgotPasswordStep`) if the caller
 * re-renders with a different step before the request settles. The backend
 * responds identically for every address, so there is no branch on the
 * outcome here - a rejection can only mean a transport failure, which the
 * caller renders via `isError`/`error`.
 */
export function useRequestPasswordReset(onRequested: () => void) {
  return useMutation({
    mutationFn: (email: string) => requestPasswordReset(email),
    onSuccess: onRequested,
  });
}

/**
 * Complete a password reset with the emailed code and new password (reset step).
 *
 * The backend collapses every OTP failure reason (wrong, expired, already
 * used, never issued) into one `OTP_INVALID` code, so `onOtpInvalid` - defined
 * here on the mutation, not at the call site - is the single place that
 * advances `ForgotPasswordStep` to `'failed'`. Any other rejection (e.g.
 * `PASSWORD_CONFIRMATION_MISMATCH`, or a transport failure) is left for the
 * caller to render inline via `isError`/`error` on the same step - the client
 * schema already blocks a mismatched submission, so a mismatch reaching the
 * server is a defensive path, not the expected one.
 */
export function useCompletePasswordReset(onOtpInvalid: () => void) {
  return useMutation({
    mutationFn: (input: CompletePasswordResetInput) => completePasswordReset(input),
    onError: (error: unknown) => {
      if (getApiErrorCode(error) === API_ERROR_CODES.OTP_INVALID) {
        onOtpInvalid();
      }
    },
  });
}

export type { CompletePasswordResetInput };
